import { ActionItemsService } from '@pique/core/modules/properties';
import { useLiveData, useService } from '@toeverything/infra';
import dayjs from 'dayjs';
import { useMemo } from 'react';

export interface ActivityDay {
  date: string;
  minutes: number;
}

export type ActivityGraphMode = 'week' | 'year';

export interface UseActivityGraphDataResult {
  days: ActivityDay[];
  totalMinutes: number;
  maxMinutes: number;
}

export const useActivityGraphData = (
  mode: ActivityGraphMode
): UseActivityGraphDataResult => {
  const actionItemsService = useService(ActionItemsService);
  const actionItemsByPage = useLiveData(actionItemsService.actionItems$);
  const focusedSessionStartedAtMs = useLiveData(
    actionItemsService.focusedSessionStartedAtMs$
  );

  return useMemo(() => {
    const today = dayjs();
    const dayCount = mode === 'week' ? 7 : 365;
    const startDate = today.subtract(dayCount - 1, 'day');

    const dateSet = new Set<string>();
    for (let i = 0; i < dayCount; i++) {
      dateSet.add(startDate.add(i, 'day').format('YYYY-MM-DD'));
    }

    const allItems = actionItemsService.query({});
    const dailyTotals = new Map<string, number>();

    for (const item of allItems) {
      const fd = item.focusDaily;
      if (!fd) continue;
      for (const [dateKey, sec] of Object.entries(fd)) {
        if (!dateSet.has(dateKey)) continue;
        const seconds = Math.max(0, Math.floor((sec ?? 0) as number));
        dailyTotals.set(dateKey, (dailyTotals.get(dateKey) ?? 0) + seconds);
      }
    }

    const days: ActivityDay[] = [];
    let totalMinutes = 0;
    let maxMinutes = 0;

    for (let i = 0; i < dayCount; i++) {
      const dateKey = startDate.add(i, 'day').format('YYYY-MM-DD');
      const totalSeconds = dailyTotals.get(dateKey) ?? 0;
      const minutes =
        totalSeconds > 0 ? Math.max(1, Math.floor(totalSeconds / 60)) : 0;
      days.push({ date: dateKey, minutes });
      totalMinutes += minutes;
      if (minutes > maxMinutes) maxMinutes = minutes;
    }

    return { days, totalMinutes, maxMinutes };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionItemsService, actionItemsByPage, focusedSessionStartedAtMs, mode]);
};
